import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://169.58.21.41/api';
const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

const CATEGORIES = [
    { id: 'all', label: 'Tümü / All', icon: '📋' },
    { id: 'sensor', label: 'Sensör', icon: '📡' },
    { id: 'field', label: 'Tarla', icon: '🌾' },
    { id: 'user', label: 'Kullanıcı', icon: '👤' },
    { id: 'irrigation', label: 'Sulama', icon: '💧' },
    { id: 'backup', label: 'Yedekleme', icon: '💾' },
    { id: 'auth', label: 'Giriş/Çıkış', icon: '🔐' },
    { id: 'api_key', label: 'API', icon: '🔑' },
    { id: 'system', label: 'Sistem', icon: '⚙️' },
];

const SEVERITY_CFG = {
    info:    { label: 'Info',    bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
    warning: { label: 'Uyarı',  bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
    error:   { label: 'Hata',   bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
};

const METHOD_CFG = {
    POST:   { bg: '#d1fae5', color: '#065f46' },
    PUT:    { bg: '#dbeafe', color: '#1e40af' },
    DELETE: { bg: '#fee2e2', color: '#991b1b' },
    PATCH:  { bg: '#ede9fe', color: '#5b21b6' },
    GET:    { bg: '#f1f5f9', color: '#475569' },
};

function fmtDate(iso) {
    return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'medium' });
}

function fmtRelative(iso) {
    const diff = Date.now() - new Date(iso).getTime();
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s önce`;
    if (s < 3600) return `${Math.floor(s / 60)}dk önce`;
    if (s < 86400) return `${Math.floor(s / 3600)}sa önce`;
    return `${Math.floor(s / 86400)}g önce`;
}

export default function AuditLog() {
    const { language } = useLanguage();
    const tr = language === 'tr';

    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [category, setCategory] = useState('all');
    const [severity, setSeverity] = useState('all');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [page, setPage] = useState(1);
    const [limit] = useState(50);

    // Expanded row
    const [expanded, setExpanded] = useState(null);

    // Auto-refresh
    const [autoRefresh, setAutoRefresh] = useState(false);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page, limit });
            if (category !== 'all') params.set('category', category);
            if (severity !== 'all') params.set('severity', severity);
            if (search) params.set('search', search);
            if (from) params.set('from', new Date(from).toISOString());
            if (to) params.set('to', new Date(to + 'T23:59:59').toISOString());

            const res = await fetch(`${API_URL}/audit?${params}`, { headers: getHeaders() });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setLogs(data.logs || []);
            setTotal(data.total || 0);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [category, severity, search, from, to, page, limit]);

    const fetchStats = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/audit/stats`, { headers: getHeaders() });
            if (res.ok) setStats(await res.json());
        } catch {}
    }, []);

    useEffect(() => {
        fetchLogs();
        fetchStats();
    }, [fetchLogs, fetchStats]);

    useEffect(() => {
        if (!autoRefresh) return;
        const t = setInterval(() => { fetchLogs(); fetchStats(); }, 10000);
        return () => clearInterval(t);
    }, [autoRefresh, fetchLogs, fetchStats]);

    // Reset page on filter change
    useEffect(() => { setPage(1); }, [category, severity, search, from, to]);

    const totalPages = Math.ceil(total / limit);

    const handleClearOld = async () => {
        if (!window.confirm(tr ? '90 günden eski kayıtlar silinsin mi?' : 'Delete logs older than 90 days?')) return;
        try {
            const res = await fetch(`${API_URL}/audit/old`, { method: 'DELETE', headers: getHeaders() });
            const data = await res.json();
            alert(tr ? `${data.deleted} kayıt silindi.` : `${data.deleted} records deleted.`);
            fetchLogs(); fetchStats();
        } catch (e) { alert(e.message); }
    };

    const sev = SEVERITY_CFG;

    return (
        <div style={{ padding: '24px 0', maxWidth: 1100 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h2 style={{ margin: '0 0 5px', fontSize: 22, fontWeight: 800 }}>
                        📋 {tr ? 'İşlem Kayıtları' : 'Audit Log'}
                    </h2>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: 13.5 }}>
                        {tr ? 'Sistemdeki tüm önemli işlemlerin zaman damgalı kaydı.' : 'Timestamped record of all significant system actions.'}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                        onClick={() => setAutoRefresh(p => !p)}
                        style={{ background: autoRefresh ? '#4f46e5' : '#e5e7eb', color: autoRefresh ? '#fff' : '#374151', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                        {autoRefresh ? '⏸ Auto' : '▶ Auto'} {tr ? 'Yenile' : 'Refresh'}
                    </button>
                    <button onClick={() => { fetchLogs(); fetchStats(); }}
                        style={{ background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                        ↻ {tr ? 'Yenile' : 'Refresh'}
                    </button>
                    <button onClick={handleClearOld}
                        style={{ background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                        🗑 {tr ? '90g+ Temizle' : 'Clear 90d+'}
                    </button>
                </div>
            </div>

            {/* Stats bar */}
            {stats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>{stats.last24h}</div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{tr ? 'Son 24 Saat' : 'Last 24h'}</div>
                    </div>
                    {Object.entries(sev).map(([k, v]) => (
                        <div key={k} style={{ background: v.bg, border: `1px solid ${v.dot}40`, borderRadius: 10, padding: '12px 14px', textAlign: 'center', cursor: 'pointer' }}
                            onClick={() => setSeverity(severity === k ? 'all' : k)}>
                            <div style={{ fontSize: 22, fontWeight: 800, color: v.color }}>{stats.bySeverity?.[k] || 0}</div>
                            <div style={{ fontSize: 11, color: v.color, marginTop: 2, fontWeight: 600 }}>{v.label}</div>
                        </div>
                    ))}
                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>{total}</div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{tr ? 'Toplam Kayıt' : 'Total'}</div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px', marginBottom: 18 }}>
                {/* Category chips */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                    {CATEGORIES.map(c => (
                        <button key={c.id} onClick={() => setCategory(c.id)}
                            style={{ background: category === c.id ? '#4f46e5' : '#e5e7eb', color: category === c.id ? '#fff' : '#374151', border: 'none', borderRadius: 20, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: category === c.id ? 700 : 500 }}>
                            {c.icon} {tr ? c.label.split('/')[0].trim() : (c.label.split('/')[1]?.trim() || c.label)}
                        </button>
                    ))}
                </div>

                {/* Search + date + severity */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ flex: '1 1 200px', position: 'relative' }}>
                        <input
                            type="text"
                            placeholder={tr ? '🔍 Arama... (kullanıcı, işlem, yol)' : '🔍 Search... (user, action, path)'}
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && setSearch(searchInput)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box' }}
                        />
                    </div>
                    <button onClick={() => setSearch(searchInput)}
                        style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        {tr ? 'Ara' : 'Search'}
                    </button>
                    {search && (
                        <button onClick={() => { setSearch(''); setSearchInput(''); }}
                            style={{ background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 12 }}>
                            ✕ {tr ? 'Temizle' : 'Clear'}
                        </button>
                    )}
                    <select value={severity} onChange={e => setSeverity(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}>
                        <option value="all">{tr ? 'Tüm Seviyeler' : 'All Severity'}</option>
                        <option value="info">Info</option>
                        <option value="warning">{tr ? 'Uyarı' : 'Warning'}</option>
                        <option value="error">{tr ? 'Hata' : 'Error'}</option>
                    </select>
                    <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                        style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
                        title={tr ? 'Başlangıç tarihi' : 'From date'} />
                    <input type="date" value={to} onChange={e => setTo(e.target.value)}
                        style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
                        title={tr ? 'Bitiş tarihi' : 'To date'} />
                    {(from || to) && (
                        <button onClick={() => { setFrom(''); setTo(''); }}
                            style={{ background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', fontSize: 12 }}>
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {/* Error */}
            {error && (
                <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 14, color: '#991b1b', fontSize: 13 }}>
                    ❌ {error}
                </div>
            )}

            {/* Log table */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead style={{ background: '#1e293b', color: '#94a3b8' }}>
                        <tr>
                            <th style={{ padding: '10px 14px', textAlign: 'left', width: 28 }}></th>
                            <th style={{ padding: '10px 14px', textAlign: 'left', width: 130 }}>{tr ? 'Zaman' : 'Time'}</th>
                            <th style={{ padding: '10px 14px', textAlign: 'left', width: 80 }}>{tr ? 'Seviye' : 'Severity'}</th>
                            <th style={{ padding: '10px 14px', textAlign: 'left', width: 80 }}>{tr ? 'Kategori' : 'Category'}</th>
                            <th style={{ padding: '10px 14px', textAlign: 'left' }}>{tr ? 'Açıklama' : 'Description'}</th>
                            <th style={{ padding: '10px 14px', textAlign: 'left', width: 160 }}>{tr ? 'Kullanıcı' : 'User'}</th>
                            <th style={{ padding: '10px 14px', textAlign: 'left', width: 60 }}>HTTP</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>
                                <div style={{ display: 'inline-block', width: 20, height: 20, borderRadius: '50%', border: '2px solid #4f46e5', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
                                <div style={{ marginTop: 8 }}>{tr ? 'Yükleniyor…' : 'Loading…'}</div>
                            </td></tr>
                        ) : logs.length === 0 ? (
                            <tr><td colSpan={7} style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
                                <div style={{ fontSize: 40, marginBottom: 8 }}>📭</div>
                                <div>{tr ? 'Kayıt bulunamadı.' : 'No records found.'}</div>
                            </td></tr>
                        ) : logs.map((log, i) => {
                            const sv = sev[log.severity] || sev.info;
                            const cat = CATEGORIES.find(c => c.id === log.category);
                            const mCfg = METHOD_CFG[log.method] || METHOD_CFG.GET;
                            const isExpanded = expanded === log.id;
                            return (
                                <React.Fragment key={log.id}>
                                    <tr
                                        onClick={() => setExpanded(isExpanded ? null : log.id)}
                                        style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.1s' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                                        onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f8fafc'}
                                    >
                                        <td style={{ padding: '8px 14px', color: '#94a3b8', fontSize: 11 }}>
                                            {isExpanded ? '▼' : '▶'}
                                        </td>
                                        <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>
                                            <div style={{ fontWeight: 600, fontSize: 12 }}>{fmtDate(log.createdAt)}</div>
                                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{fmtRelative(log.createdAt)}</div>
                                        </td>
                                        <td style={{ padding: '8px 14px' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: sv.dot, flexShrink: 0 }} />
                                                <span style={{ background: sv.bg, color: sv.color, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20 }}>
                                                    {sv.label}
                                                </span>
                                            </span>
                                        </td>
                                        <td style={{ padding: '8px 14px' }}>
                                            <span style={{ fontSize: 12 }}>{cat?.icon || '•'} {cat ? (tr ? cat.label.split('/')[0].trim() : (cat.label.split('/')[1]?.trim() || cat.label)) : log.category}</span>
                                        </td>
                                        <td style={{ padding: '8px 14px' }}>
                                            <div style={{ fontWeight: 600, marginBottom: 1 }}>{log.description}</div>
                                            <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{log.action}</div>
                                        </td>
                                        <td style={{ padding: '8px 14px' }}>
                                            {log.userEmail ? (
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>{log.userEmail}</div>
                                                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{log.userRole}</div>
                                                </div>
                                            ) : (
                                                <span style={{ color: '#d1d5db', fontSize: 11 }}>—</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '8px 14px' }}>
                                            {log.method && (
                                                <span style={{ background: mCfg.bg, color: mCfg.color, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, fontFamily: 'monospace' }}>
                                                    {log.method}
                                                </span>
                                            )}
                                            {log.statusCode && (
                                                <div style={{ fontSize: 10, color: log.statusCode >= 400 ? '#ef4444' : '#6b7280', marginTop: 2, fontFamily: 'monospace' }}>
                                                    {log.statusCode}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr style={{ background: '#f0f9ff' }}>
                                            <td colSpan={7} style={{ padding: '12px 20px 14px 48px' }}>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, fontSize: 12 }}>
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: '#475569', marginBottom: 4 }}>🔗 {tr ? 'Yol' : 'Path'}</div>
                                                        <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: 4, fontSize: 11, wordBreak: 'break-all' }}>{log.path || '—'}</code>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: '#475569', marginBottom: 4 }}>🌐 IP</div>
                                                        <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>{log.ip || '—'}</code>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: '#475569', marginBottom: 4 }}>🕐 {tr ? 'Tam Zaman' : 'Full Timestamp'}</div>
                                                        <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>{log.createdAt}</code>
                                                    </div>
                                                    {log.meta && (
                                                        <div style={{ gridColumn: '1 / -1' }}>
                                                            <div style={{ fontWeight: 700, color: '#475569', marginBottom: 4 }}>📦 {tr ? 'Detaylar' : 'Meta'}</div>
                                                            <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '10px 14px', borderRadius: 8, fontSize: 11, overflowX: 'auto', margin: 0, maxHeight: 200, overflowY: 'auto' }}>
                                                                {JSON.stringify(log.meta, null, 2)}
                                                            </pre>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 10 }}>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                        {tr ? `${total} kayıttan` : `${total} records,`} {tr ? 'sayfa' : 'page'} {page} / {totalPages}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setPage(1)} disabled={page === 1}
                            style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1, fontSize: 13 }}>«</button>
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                            style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1, fontSize: 13 }}>‹</button>
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                            let p = page - 2 + i;
                            if (p < 1) p = i + 1;
                            if (p > totalPages) return null;
                            return (
                                <button key={p} onClick={() => setPage(p)}
                                    style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid', borderColor: p === page ? '#4f46e5' : '#e5e7eb', background: p === page ? '#4f46e5' : '#fff', color: p === page ? '#fff' : '#374151', cursor: 'pointer', fontWeight: p === page ? 700 : 400, fontSize: 13 }}>
                                    {p}
                                </button>
                            );
                        })}
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                            style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1, fontSize: 13 }}>›</button>
                        <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                            style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1, fontSize: 13 }}>»</button>
                    </div>
                </div>
            )}

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}
